import { useEffect, useRef, useState } from 'react';
import braintree from 'braintree-web';
import { PaymentConfig } from '@v2/shared/lib/types';
import usePayment from '../usePayment';
import createBraintreeSession from '../createBraintreeSession';
import { REVEALED, WalletReveal } from '../useWalletReveal';
import { SubmitPayload, PaymentCart, WalletShape } from '../types';
import loadGooglePayScript from './loadGooglePayScript';

type Props = {
  payment: PaymentConfig;
  cart: PaymentCart;
  submitPayload: SubmitPayload;
  /** This button's gate in the row it sits in — `reveal.secondary`, which opens once Apple Pay has decided. */
  reveal?: WalletReveal;
  /** The corner the row is drawn with — see `WalletShape`. */
  shape?: WalletShape;
};

/**
 * The row's shape, as Google's sdk takes it: a radius in pixels, which is the only lever
 * it offers. 1000 is how a pill is asked for; 4 is `rounded`, and is the number the legacy
 * cart's own button passes.
 */
const BUTTON_RADIUS: Record<WalletShape, number> = { pill: 1000, rounded: 4 };

/**
 * Google's provinces come back as codes and jshop's address book stores names.
 * US states are already two-letter on both sides, which is why only CA is listed.
 */
const ADMINISTRATIVE_AREAS: Record<string, Record<string, string>> = {
  CA: {
    BC: 'British Columbia',
    AB: 'Alberta',
    SK: 'Saskatchewan',
    MB: 'Manitoba',
    ON: 'Ontario',
    QC: 'Quebec',
    NB: 'New Brunswick',
    NS: 'Nova Scotia',
    PE: 'Prince Edward Island',
    NF: 'Newfoundland',
    YT: 'Yukon',
    NT: 'Northwest Territories',
    NU: 'Nunavut',
  },
};

/**
 * Google Pay, the sibling of ApplePay and on the same terms: shared, configured from
 * `payment`, and invisible unless Google says the visitor can actually pay with it.
 *
 * The button itself is drawn by Google's SDK rather than by us — brand rules — so this
 * renders an empty div and hands it to `createButton`.
 *
 * Where it differs from Apple: the sheet asks *us* to reprice through one callback
 * (`onPaymentDataChanged`) instead of three, and that callback has to be handed to the
 * PaymentsClient at construction rather than to the sheet — so it is fixed for the life
 * of the client and cannot be re-registered when the order changes.
 *
 * It therefore reads the order through `cartRef`, the same way Apple's three callbacks
 * do, and the client is built exactly once. It used to be rebuilt instead, keyed on
 * `cart.items[0].sku.code`, which was serviceable while the only caller was a product
 * page whose order is one line: the key changed precisely when the price did. The sale
 * page's drawer is a real cart and it breaks both ways — removing the third line leaves
 * the key untouched, so the sheet would have repriced a cart that no longer existed,
 * while removing the *first* line would have torn down and rebuilt the whole client
 * (pay.js, a Braintree instance, `isReadyToPay`) to no purpose. A ref has neither
 * failure and is what lets the buttons be drawn once and follow the cart, rather than
 * being re-initialized on every change the way the legacy sidebar does it.
 *
 * Repricing is only wired up when we ship domestically from the US. Outside it there is
 * one flat rate and no options to choose between, so asking the sheet to call back would
 * buy a round trip that changes nothing — headless made the same call.
 */
export default function GooglePay({
  payment,
  cart,
  submitPayload,
  reveal = REVEALED,
  shape = 'pill',
}: Props) {
  const [isReady, setIsReady] = useState(false);
  /** That the question has been answered, either way — see the same state in ApplePay. */
  const [hasSettled, setHasSettled] = useState(false);
  /** The page's device data collection, still running — see the same ref in ApplePay. */
  const deviceData = useRef<Promise<string | null> | null>(null);
  const [googlePayInstance, setGooglePayInstance] = useState<any>(null);
  const [paymentsClient, setPaymentsClient] = useState<any>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  /**
   * The order as it stands right now, for the two callbacks that outlive the render that
   * registered them — `onPaymentDataChanged`, fixed at the client's construction, and the
   * click handler Google's own button holds. Both would otherwise price whichever cart
   * was current when they were created. Apple's callbacks read a ref for the same reason.
   */
  const cartRef = useRef<PaymentCart>(cart);
  /**
   * Whether this button already has a sheet up — the same reentrancy guard `openSheet` is
   * in ApplePay, and there for the same reported bug: a fast double-tap asks for a second
   * payment sheet while the first is still open, and the browser refuses it with
   * `InvalidAccessError: Page already has an active payment session.`
   *
   * A boolean rather than the sheet itself only because there is no sheet object to hold:
   * `loadPaymentData` hands back the payment, not a session. The promise it returns is the
   * sheet's whole life, so unlike Apple's — which ends through four separate callbacks —
   * this one is released in a single `finally`.
   *
   * A ref and not state, for the reason ApplePay gives: it has to be true within the click
   * handler that sets it, and a re-render would not have committed before the second tap
   * of a double-tap arrives. Disabling the button would not work here anyway, since the
   * button is Google's and this component does not render it.
   */
  const isSheetOpen = useRef(false);
  const {
    calculateShipping,
    calculateTaxes,
    calculateCartTotal,
    formatAmount,
    getShippingOptions,
    getSelectedShippingOption,
    getShippingOptionDescription,
  } = usePayment();

  const isUS = payment.shippingCountry === 'US';

  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      try {
        // Started together, because neither knows about the other: pay.js is a script on
        // Google's cdn and the session is a round trip to Braintree. Awaited one after the
        // other they cost the visitor both, and on the device this button is *for* there is
        // nobody else to start the second one — Apple Pay, which normally opens the session
        // first and leaves this one nothing to wait for, gives up before it asks on any
        // browser that is not Safari.
        const [, session] = await Promise.all([
          loadGooglePayScript(),
          createBraintreeSession(payment.clientToken),
        ]);

        if (cancelled || !window.google?.payments?.api) {
          return;
        }

        const client = new window.google.payments.api.PaymentsClient({
          environment: payment.environment === 'production' ? 'PRODUCTION' : 'TEST',
          paymentDataCallbacks: isUS ? { onPaymentDataChanged } : {},
        });

        const instance: any = await braintree.googlePayment.create({
          client: session.client,
          googlePayVersion: 2,
          googleMerchantId: payment.googleMerchantId,
        });

        // `existingPaymentMethodRequired` is what makes this "can this visitor pay"
        // rather than "does this browser support Google Pay" — without it the button
        // shows in every Chrome and opens onto an empty wallet.
        const readiness = await client.isReadyToPay({
          apiVersion: 2,
          apiVersionMinor: 0,
          existingPaymentMethodRequired: true,
          allowedPaymentMethods: instance.createPaymentDataRequest().allowedPaymentMethods,
        });

        if (cancelled || !readiness.result) {
          return;
        }

        deviceData.current = session.deviceData;
        setGooglePayInstance(instance);
        setPaymentsClient(client);
        setIsReady(true);
      } catch (error) {
        // Console only: the Add to Cart button beside this one is the fallback.
        console.error('Could not initialize Google Pay', error);
      } finally {
        // Including the `cancelled` exit above — an unmount mid-setup is an answer of a
        // sort, and the row cannot be left waiting on a run that was abandoned.
        setHasSettled(true);
      }
    };

    initialize();

    return () => {
      cancelled = true;
    };
    // Once, for the life of the component. Nothing built here depends on what is being
    // bought — the merchant, the gateway and the wallet's readiness are all properties of
    // the site and the visitor — and the order itself is read through cartRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Reported once, from the state transition rather than from the setup. */
  useEffect(() => {
    if (hasSettled) {
      reveal.reportSettled();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSettled]);

  useEffect(() => {
    if (!buttonRef.current) {
      return;
    }

    // Cleared first, so a re-run cannot leave two buttons stacked in the div.
    buttonRef.current.innerHTML = '';

    if (!paymentsClient) {
      return;
    }

    buttonRef.current.appendChild(
      paymentsClient.createButton({
        onClick: payWithGooglePay,
        buttonSizeMode: 'fill',
        buttonRadius: BUTTON_RADIUS[shape],
      })
    );
    // `isReady` is a dependency because it is what puts the div on the page: until it
    // turns true this returns null, so `buttonRef.current` is null and the effect can
    // only bail. It and `paymentsClient` are set from the same continuation and React
    // normally batches them into one commit, which is why this works most of the time —
    // but anything that splits them (a client already warm, so the awaits resolve in
    // different ticks) lands `paymentsClient` in a commit where there is still no div,
    // and the run that finally mounts one changes neither of the old dependencies. The
    // button is then never drawn at all. A page whose format can change recovered by
    // accident on the next selection; a single-sku page had nothing to recover on, which
    // is exactly where it was reported.
    //
    // `reveal.isRevealed` is here for exactly the same reason and is now the usual one:
    // in a shared row it, not `isReady`, is the commit that finally mounts the div.
    //
    // The cart is deliberately *not* a dependency: the button is drawn once and the click
    // handler follows the cart through cartRef, so redrawing it on every add and remove
    // would only make Google's own button flicker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentsClient, isReady, reveal.isRevealed]);

  function buildDisplayItems(current: PaymentCart) {
    const items = [
      { label: 'Subtotal', type: 'SUBTOTAL', price: formatAmount(current.total_items_price) },
    ];

    if (current.total_discounts > 0) {
      items.push({ label: 'Discount', type: 'DISCOUNT', price: formatAmount(-current.total_discounts) });
    }

    if (current.tax_total_amount > 0) {
      items.push({ label: 'Estimated Tax', type: 'TAX', price: formatAmount(current.tax_total_amount) });
    }

    const shippingOption = getSelectedShippingOption(current);

    items.push({
      label: `Shipping: ${shippingOption.name}`,
      type: 'SHIPPING_OPTION',
      price: formatAmount(shippingOption.cost),
    });

    return items;
  }

  async function onPaymentDataChanged(intermediatePaymentData: any) {
    const invalid = {
      error: {
        reason: 'SHIPPING_ADDRESS_INVALID',
        message: 'This selected shipping address is invalid.',
        intent: 'SHIPPING_ADDRESS',
      },
    };

    if (!intermediatePaymentData.shippingAddress) {
      return invalid;
    }

    try {
      const address = {
        city: intermediatePaymentData.shippingAddress.locality,
        state: intermediatePaymentData.shippingAddress.administrativeArea,
        zip: intermediatePaymentData.shippingAddress.postalCode,
        country_iso2: String(intermediatePaymentData.shippingAddress.countryCode).toUpperCase(),
      };

      let current = await calculateShipping(
        cartRef.current,
        address,
        intermediatePaymentData.shippingOptionData?.id
      );
      current = await calculateTaxes(current, address);

      return {
        newTransactionInfo: {
          displayItems: buildDisplayItems(current),
          currencyCode: 'USD',
          countryCode: 'US',
          totalPriceStatus: 'ESTIMATED',
          totalPriceLabel: 'Total',
          totalPrice: formatAmount(calculateCartTotal(current)),
          checkoutOption: 'DEFAULT',
        },
        newShippingOptionParameters: {
          defaultSelectedOptionId: getSelectedShippingOption(current).id,
          shippingOptions: getShippingOptions(current).map((option) => ({
            id: option.id,
            label: option.name,
            description: getShippingOptionDescription(option),
          })),
        },
      };
    } catch (error) {
      console.error('Could not price the Google Pay shipping address', error);

      return invalid;
    }
  }

  async function payWithGooglePay(event: Event) {
    event.preventDefault();

    if (!googlePayInstance || !paymentsClient || isSheetOpen.current) {
      return;
    }

    // Read once, so the sheet that opens and the payload that closes it describe the
    // same order even if the drawer changes underneath.
    const current = cartRef.current;

    const paymentDataRequest = googlePayInstance.createPaymentDataRequest({
      emailRequired: true,
      transactionInfo: {
        currencyCode: 'USD',
        totalPriceStatus: 'ESTIMATED',
        totalPrice: formatAmount(calculateCartTotal(current)),
      },
    });

    // Braintree builds the request without a billing address; checkout needs one to
    // charge against, so it is asked for here rather than left to the gateway.
    const cardPaymentMethod = paymentDataRequest.allowedPaymentMethods[0];
    cardPaymentMethod.parameters.billingAddressRequired = true;
    cardPaymentMethod.parameters.billingAddressParameters = { format: 'FULL', phoneNumberRequired: true };

    if (paymentDataRequest.merchantInfo) {
      paymentDataRequest.merchantInfo.merchantName = payment.merchantName;
    }

    // Claimed on the last line before the await, so the second tap of a double-tap — which
    // runs synchronously, in the same task, and gets this far before the first tap has
    // done anything asynchronous — finds it already taken.
    isSheetOpen.current = true;

    try {
      const paymentData = await paymentsClient.loadPaymentData({
        ...paymentDataRequest,
        callbackIntents: isUS ? ['SHIPPING_ADDRESS', 'SHIPPING_OPTION'] : [],
        shippingAddressRequired: true,
        shippingAddressParameters: { allowedCountryCodes: [payment.shippingCountry] },
        shippingOptionRequired: isUS,
        transactionInfo: {
          ...paymentDataRequest.transactionInfo,
          countryCode: 'US',
          displayItems: buildDisplayItems(current),
          totalPriceLabel: 'Total',
          checkoutOption: 'DEFAULT',
        },
      });

      const parsed = await googlePayInstance.parseResponse(paymentData);
      const shipping = paymentData.shippingAddress;
      const billing = paymentData?.paymentMethodData?.info?.billingAddress;

      if (!shipping || !billing) {
        console.error('Google Pay returned no shipping or billing address');

        return;
      }

      const payload = {
        ...parsed,
        // Top level, and awaited only now — see the same line in ApplePay for why.
        deviceData: await deviceData.current,
        details: {
          ...parsed.details,
          email: paymentData.email,
          shippingAddress: toPayloadAddress(shipping, payment.shippingCountry),
          billingAddress: toPayloadAddress(billing, payment.shippingCountry),
          shippingOptionId: paymentData.shippingOptionData?.id ?? null,
        },
      };

      await submitPayload(JSON.stringify(payload));
    } catch (error) {
      // Includes the visitor closing the sheet, which the SDK also rejects with.
      console.error('Could not complete the Google Pay payment', error);
    } finally {
      // Every way the sheet can end settles that promise — authorized, dismissed, or
      // refused — so this one line is the whole release, and the button works again on a
      // legitimate retry. It covers the early `return` above as well, which is the path a
      // sheet that came back without an address takes.
      isSheetOpen.current = false;
    }
  }

  if (!isReady || !reveal.isRevealed) {
    return null;
  }

  return <div ref={buttonRef} />;
}

/** Google's address shape, in the shape checkout stores an address in. */
function toPayloadAddress(address: any, shippingCountry: string) {
  const [forename, ...rest] = String(address.name || '').split(' ');

  return {
    forename,
    // Everything after the first space, so a middle name does not become the surname.
    surname: rest.join(' ') || undefined,
    line1: address.address1,
    line2: address.address2,
    city: address.locality,
    state: (ADMINISTRATIVE_AREAS[shippingCountry] || {})[address.administrativeArea] || address.administrativeArea,
    postalCode: address.postalCode,
    countryCode: String(address.countryCode || '').toUpperCase(),
  };
}
